import React, { useState, useEffect, useMemo } from 'react';
import '../../components/UserDetail/UserDetail.css';
import { FaUserCircle } from 'react-icons/fa';
import { VscGraphLine } from "react-icons/vsc";
import { HiOutlineCube } from "react-icons/hi";
import { BiLogOutCircle } from "react-icons/bi";
import { getAuthToken, removeAuthToken, getUserData } from "../../utils/auth";
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserDetails } from "../../API/Api";
import NotVerifiedModal from '../DashboardModule/NotVerifiedModal';
import VerifyAccountModal from '../VerifyAccountModal/VerifyAccountModal';
import logostelmart from '../../assets/images/logomain.png';

const SidebarTab = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showNotVerifiedModal, setShowNotVerifiedModal] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const storedUserData = useMemo(() => getUserData(), []);

    // Determine active tab based on current route
    const getActiveTab = () => {
        const path = location.pathname;
        if (path.includes('/business')) return 'business';
        if (path.includes('/address')) return 'address';
        if (path.includes('/settings')) return 'settings';
        return 'business'; // default
    };
    const getUserInitials = (firstName, lastName) => {
        let initials = '';
        if (firstName?.trim()) initials += firstName.trim()[0].toUpperCase();
        if (lastName?.trim()) initials += lastName.trim()[0].toUpperCase();
        return initials || 'U';
    };


    const [activeTab, setActiveTab] = useState(getActiveTab());

    useEffect(() => {
        setActiveTab(getActiveTab());
    }, [location.pathname]);

    useEffect(() => {
        const token = getAuthToken();
        if (!token) {
            toast.error("Please login to access dashboard");
            navigate("/");
            return;
        }

        const fetchUser = async () => {
            try {
                const userData = await getUserDetails();
                setUser(userData.user);

                if (userData.user?.verified !== 1) {
                    // setShowNotVerifiedModal(true);
                    const interval = setInterval(() => {
                        setShowNotVerifiedModal(true);
                    }, 30000);
                    return () => clearInterval(interval);
                }
            } catch (error) {
                console.error("Error fetching user details:", error);
                toast.error("Failed to load user data");
                if (error.response?.status === 401) {
                    removeAuthToken();
                    navigate("/");
                }
            }
        };

        fetchUser();
    }, [navigate, storedUserData]);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        navigate(`/${tab}`);
    };

    const handleLogout = () => {
        removeAuthToken();
        toast.success("You have been logged out successfully");
        setTimeout(() => {
            window.location.href = "/";
        }, 1000);
    };

    if (!storedUserData) {
        return (
            <div className="access-denied2">
                <h2>Loading user data...</h2>
            </div>
        );
    }

    const truncateText = (text, wordLimit = 5) => {
        if (!text) return '';
        const words = text.split(' ');
        return words.length > wordLimit
            ? words.slice(0, wordLimit).join(' ') + '...'
            : text;
    };

    const renderBuyerSidebar = () => (
        <aside className="sidebar-three">
            <div className="user-info">
                {user?.image ? (
                    <img src={user.image} alt="User" className="user-avatar" />
                ) : (
                    <div className="user-initials">
                        {getUserInitials(user?.first_name, user?.last_name)}
                    </div>
                )}
                <div className="user-details">
                    <h3 title={user?.first_name}>{truncateText(user?.first_name) || 'User'}</h3>
                    <p title={user?.ph_number}>{truncateText(user?.ph_number)}</p>
                </div>
            </div>
            {user?.stelmart_status === 'Approved' ? (
                <button className="verify-btn-success">
                    <img src={logostelmart} alt="Logo Stelmart" width="30" height="30" />
                    Verified Account
                </button>
            ) : user?.stelmart_status === 'Pending' && user?.verified == 1? (
                <button className="verify-btn">
                    Pending Account
                </button>
            ) : (
                <button className="verify-btn" onClick={openModal}>
                    Verify Account
                </button>
            )}


            <nav className="nav-links">
                <ul>
                    <li
                        onClick={() => handleTabClick("business")}
                        className={activeTab === "business" ? "active" : ""}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="icon bi bi-buildings"
                            viewBox="0 0 16 16"
                        >
                            <path d="M14.763.075A.5.5 0 0 1 15 .5v15a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5V14h-1v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10a.5.5 0 0 1 .342-.474L6 7.64V4.5a.5.5 0 0 1 .276-.447l8-4a.5.5 0 0 1 .487.022M6 8.694 1 10.36V15h5zM7 15h2v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5V15h2V1.309l-7 3.5z" />
                            <path d="M2 11h1v1H2zm2 0h1v1H4zm-2 2h1v1H2zm2 0h1v1H4zm4-4h1v1H8zm2 0h1v1h-1zm-2 2h1v1H8zm2 0h1v1h-1zm2-2h1v1h-1zm0 2h1v1h-1zM8 7h1v1H8zm2 0h1v1h-1zm2 0h1v1h-1zM8 5h1v1H8zm2 0h1v1h-1zm2 0h1v1h-1zm0-2h1v1h-1z" />
                        </svg>
                        My Business
                    </li>

                    <li
                        onClick={() => handleTabClick("address")}
                        className={activeTab === "address" ? "active" : ""}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="icon bi bi-pin-map"
                            viewBox="0 0 16 16"
                        >
                            <path
                                fillRule="evenodd"
                                d="M3.1 11.2a.5.5 0 0 1 .4-.2H6a.5.5 0 0 1 0 1H3.75L1.5 15h13l-2.25-3H10a.5.5 0 0 1 0-1h2.5a.5.5 0 0 1 .4.2l3 4a.5.5 0 0 1-.4.8H.5a.5.5 0 0 1-.4-.8z"
                            />
                            <path
                                fillRule="evenodd"
                                d="M8 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6M4 4a4 4 0 1 1 4.5 3.969V13.5a.5.5 0 0 1-1 0V7.97A4 4 0 0 1 4 3.999z"
                            />
                        </svg>
                        My Address
                    </li>

                    <li
                        onClick={() => handleTabClick("setting")}
                        className={activeTab === "setting" ? "active" : ""}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="icon bi bi-gear"
                            viewBox="0 0 16 16"
                        >
                            <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0" />
                            <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z" />
                        </svg>
                        Settings
                    </li>

                    <li className="logout" onClick={handleLogout}>
                        <BiLogOutCircle className="icon" />
                        Logout
                    </li>
                </ul>
            </nav>
        </aside>
    );

    const renderSellerSidebar = () => (
        <aside className="sidebar-three">
            <div className="user-info">
                {user?.image ? (
                    <img src={user.image} alt="User" className="user-avatar" />
                ) : (
                    <div className="user-initials">
                        {getUserInitials(user?.first_name, user?.last_name)}
                    </div>
                )}
                <div className="user-details">
                    <h3 title={user?.first_name}>{truncateText(user?.first_name) || 'User'}</h3>
                    <p title={user?.ph_number}>{truncateText(user?.ph_number)}</p>
                </div>
            </div>
            {user?.verified === 1 ? (
                <button className="verify-btn-success">
                    <img src={logostelmart} alt="Logo Stelmart" width="30" height="30" />
                    Verified Account
                </button>
            ) : (
                <button className="verify-btn" onClick={openModal}>
                    Verify Account
                </button>
            )}

            <nav className="nav-links">
                <ul>
                    <li
                        onClick={() => handleTabClick("seller-business")}
                        className={activeTab === "seller-business" ? "active" : ""}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="icon bi bi-buildings"
                            viewBox="0 0 16 16"
                        >
                            <path d="M14.763.075A.5.5 0 0 1 15 .5v15a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5V14h-1v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10a.5.5 0 0 1 .342-.474L6 7.64V4.5a.5.5 0 0 1 .276-.447l8-4a.5.5 0 0 1 .487.022M6 8.694 1 10.36V15h5zM7 15h2v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5V15h2V1.309l-7 3.5z" />
                            <path d="M2 11h1v1H2zm2 0h1v1H4zm-2 2h1v1H2zm2 0h1v1H4zm4-4h1v1H8zm2 0h1v1h-1zm-2 2h1v1H8zm2 0h1v1h-1zm2-2h1v1h-1zm0 2h1v1h-1zM8 7h1v1H8zm2 0h1v1h-1zm2 0h1v1h-1zM8 5h1v1H8zm2 0h1v1h-1zm2 0h1v1h-1zm0-2h1v1h-1z" />
                        </svg>
                        My Business
                    </li>
                    <li
                        onClick={() => handleTabClick("address")}
                        className={activeTab === "address" ? "active" : ""}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="icon bi bi-pin-map"
                            viewBox="0 0 16 16"
                        >
                            <path
                                fillRule="evenodd"
                                d="M3.1 11.2a.5.5 0 0 1 .4-.2H6a.5.5 0 0 1 0 1H3.75L1.5 15h13l-2.25-3H10a.5.5 0 0 1 0-1h2.5a.5.5 0 0 1 .4.2l3 4a.5.5 0 0 1-.4.8H.5a.5.5 0 0 1-.4-.8z"
                            />
                            <path
                                fillRule="evenodd"
                                d="M8 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6M4 4a4 4 0 1 1 4.5 3.969V13.5a.5.5 0 0 1-1 0V7.97A4 4 0 0 1 4 3.999z"
                            />
                        </svg>
                        My Address
                    </li>
                    <li
                        onClick={() => handleTabClick("setting")}
                        className={activeTab === "setting" ? "active" : ""}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="icon bi bi-gear"
                            viewBox="0 0 16 16"
                        >
                            <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0" />
                            <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z" />
                        </svg>
                        Settings
                    </li>
                    <li className="logout" onClick={handleLogout}>
                        <BiLogOutCircle className="icon" />
                        Logout
                    </li>
                </ul>
            </nav>
        </aside>
    );

    return (
        <div className="verification-container">
            <ToastContainer position="top-right" autoClose={5000} />

            {showNotVerifiedModal && user?.verified !== 1 && (
                <NotVerifiedModal
                    onClose={() => setShowNotVerifiedModal(false)}
                />
            )}

            <VerifyAccountModal
                isOpen={isModalOpen}
                onClose={closeModal}
                user={user}
            />

            {storedUserData?.userType === 'buyer' && renderBuyerSidebar()}
            {storedUserData?.userType === 'seller' && renderSellerSidebar()}
        </div>
    );
};

export default SidebarTab;